import React from 'react';
import { NextRouter, useRouter, withRouter } from 'next/router';
import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import palette from '@ui/palette';
import Icon from '@ui/Icon';
import {
  breadcrumbRoutes,
  DrawerNavItem,
  routeIcons,
} from '../Navigation/routes';
import { getBasePathFromRoute } from '../Navigation/NavigationTabs';
import Breadcrumb from './Breadcrumb';
import { useStudioRoutesQuery } from '../Navigation/operation.generated';

const useStyles = makeStyles()(theme => ({
  root: {
    height: '100%',
    margin: theme.spacing(0, 1.5),
  },
  container: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    height: '100%',
    overflow: 'hidden',
    maxWidth: '100%',
  },
}));

export type BreadcrumbItem = DrawerNavItem;

interface BaseBreadcrumbsProps {
  children: React.ReactNode;
  router: NextRouter;
}

/**
 * Component to only render initial root breadcrumb
 * Takes in children to render next to root
 */
export const BaseBreadcrumbs = ({ children, router }: BaseBreadcrumbsProps) => {
  const { classes } = useStyles();
  const { data: routesData } = useStudioRoutesQuery();
  const basePath = getBasePathFromRoute(router?.route || '');
  const allRoutes = [
    ...(routesData?.studioRoutes.map<DrawerNavItem>(item => ({
      route: item.route,
      label: item.label,
      iconType: routeIcons[item.label as keyof typeof routeIcons],
    })) || []),
    ...breadcrumbRoutes,
  ];
  const root = allRoutes.find(r => r.route === basePath);

  if (!root) return null;
  return (
    <Box component="nav" aria-label="breadcrumb" className={classes.root}>
      <Box component="ol" className={classes.container}>
        <React.Fragment key={root.route}>
          <Breadcrumb route={root} disableLink={root.disableLink} />
          <Icon type="chevronRight" style={{ color: palette.gray_300 }} />
        </React.Fragment>
        {children}
      </Box>
    </Box>
  );
};

/**
 * Only displays the root path w icon
 * Renders children next to chevron icon
 */
export const RootBreadcrumb = withRouter<BaseBreadcrumbsProps>(BaseBreadcrumbs);

/**
 * The dashboard's root crumb for the current section. The Studio page composes
 * its own trail with `StudioBreadcrumbs` instead of rendering this.
 */
export default function Breadcrumbs() {
  const { data: routesData } = useStudioRoutesQuery();
  const { classes } = useStyles();
  const router = useRouter();
  const basePath = getBasePathFromRoute(router?.route || '');
  const allRoutes = [
    ...(routesData?.studioRoutes.map<DrawerNavItem>(item => ({
      route: item.route,
      label: item.label,
      iconType: routeIcons[item.label as keyof typeof routeIcons],
    })) || []),
    ...breadcrumbRoutes,
  ];
  const currentRoute = allRoutes.find(r => r.route === basePath);

  if (!router || !currentRoute) {
    return null;
  }

  return (
    <Box component="nav" aria-label="breadcrumb" className={classes.root}>
      <Box component="ol" className={classes.container}>
        <Breadcrumb
          route={currentRoute}
          disableLink={currentRoute.disableLink}
        />
      </Box>
    </Box>
  );
}
